import type { StudioDto } from '@glory/contracts';
import { t, tDynamic } from '@glory/i18n';
import { RU_TIME_ZONES } from '@glory/kernel';
import {
  DEFAULT_BRAND_COLOR,
  isBrandColor,
  LEGAL_FORMS,
  normalizeStudio,
  type StudioRequisites,
  studioIssues,
} from '@glory/org/domain';
import {
  Callout,
  ContentSwap,
  Field,
  formError,
  notify,
  PageHeader,
  SettingsSection,
  Skeleton,
  SkeletonRows,
  TextInput,
  type UploadedFile,
  useAppForm,
} from '@glory/ui';
import { isEmail, normalizeEmail } from '@glory/ui/fields/parsers';
import { ORPCError } from '@orpc/client';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { z } from 'zod';

import { client, orpc } from '../../../lib/orpc.ts';
import { requireSettingsPermission, usePermissions } from '../../../lib/permissions.ts';
import { loadStudioAddresses, loadStudioBanks, loadStudioCompanies } from '../../../lib/requisites.ts';
import { uploadFile } from '../../../lib/upload.ts';

export const Route = createFileRoute('/_app/settings/studio')({
  beforeLoad: requireSettingsPermission('studio.manage'),
  component: StudioPage,
});

const TEXT_FIELDS = [
  'fullName',
  'shortName',
  'inn',
  'kpp',
  'ogrn',
  'legalAddress',
  'bankBik',
  'bankName',
  'bankCorrespondentAccount',
  'bankAccount',
  'signerName',
  'signerPosition',
  'signerBasis',
  'email',
  'website',
] as const;

type TextField = (typeof TEXT_FIELDS)[number];

const schema = z
  .object({
    ...(Object.fromEntries(TEXT_FIELDS.map((field) => [field, z.string()])) as Record<
      TextField,
      z.ZodString
    >),
    legalForm: z.enum(LEGAL_FORMS),
    phone: z.string().nullable(),
    timezone: z.enum(RU_TIME_ZONES),
    brandColor: z.string(),
    logo: z.array(z.custom<UploadedFile>()),
    signerUserId: z.string().nullable(),
  })
  .superRefine((values, ctx) => {
    // Те же проверки, что на сервере: контрольные числа ИНН, ОГРН, счёт вместе с БИК.
    for (const issue of studioIssues(normalizeStudio(toRequisites(values)))) {
      ctx.addIssue({ code: 'custom', path: [issue.field], message: tDynamic(`validation.${issue.issue}`) });
    }
    if (values.email.trim() !== '' && !isEmail(normalizeEmail(values.email))) {
      ctx.addIssue({ code: 'custom', path: ['email'], message: t('fields.emailInvalid') });
    }
  });

type Values = z.infer<typeof schema>;

function toRequisites(values: Values): StudioRequisites {
  const text = Object.fromEntries(
    TEXT_FIELDS.map((field) => [field, values[field] === '' ? null : values[field]]),
  );
  return {
    ...(text as Record<TextField, string | null>),
    legalForm: values.legalForm,
    phone: values.phone,
    timezone: values.timezone,
    brandColor: values.brandColor,
  };
}

function toValues(studio: StudioDto, logo: UploadedFile | null): Values {
  const text = Object.fromEntries(TEXT_FIELDS.map((field) => [field, studio[field] ?? '']));
  return {
    ...(text as Record<TextField, string>),
    legalForm: studio.legalForm,
    phone: studio.phone,
    timezone: studio.timezone,
    brandColor: studio.brandColor,
    logo: logo ? [logo] : [],
    signerUserId: studio.signerUserId,
  };
}

/** Логотип для поля файла: имя, размер и ссылка на превью по привязке файла к студии. */
async function loadLogo(studio: StudioDto): Promise<UploadedFile | null> {
  if (!studio.logoFileId) return null;
  const files = await client.files.list({ entityType: 'studio', entityId: studio.id });
  const file = files.find((f) => f.id === studio.logoFileId);
  if (!file) return null;
  // SVG и прочие «активные» типы не отдаются inline (по ссылке открывается скачивание),
  // но в <img> картинка рендерится без исполнения скриптов — превью берём по ссылке на скачивание.
  const previewUrl = file.mime.startsWith('image/')
    ? (await client.files.link({ fileId: file.id, inline: file.previewable })).url
    : null;
  return { id: file.id, name: file.name, size: file.size, mime: file.mime, previewUrl };
}

function StudioPage() {
  const studio = useQuery(orpc.org.studio.get.queryOptions());
  const logo = useQuery({
    queryKey: ['studio-logo', studio.data?.logoFileId ?? null],
    queryFn: () => (studio.data ? loadLogo(studio.data) : null),
    enabled: studio.isSuccess,
  });
  // Загрузка, сбой и форма проявляются друг из друга, без скачка высоты.
  const phase = studio.isPending || logo.isPending ? 'loading' : studio.isError ? 'error' : 'ready';
  return (
    <div className="flex flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader title={t('studio.title')} />
      <p className="-mt-2 text-muted">{t('studio.lead')}</p>
      <ContentSwap swapKey={phase} contentClassName="flex flex-col gap-4">
        {studio.isPending || logo.isPending ? (
          <>
            <Skeleton className="h-48" />
            <SkeletonRows rows={4} />
          </>
        ) : studio.isError ? (
          <Callout>{studio.error.message}</Callout>
        ) : (
          <StudioForm key={studio.data.version} studio={studio.data} logo={logo.data ?? null} />
        )}
      </ContentSwap>
    </div>
  );
}

/**
 * Образец фирменного стиля: так цвет, логотип и шрифт Inter выглядят в КП и на
 * странице клиента. Внутренний интерфейс остаётся в стиле «Контраст».
 */
function BrandSample({ color, logoUrl, name }: { color: string; logoUrl: string | null; name: string }) {
  return (
    <div className="flex min-w-0 flex-col overflow-hidden rounded-md border border-border">
      <div className="h-2" style={{ backgroundColor: color }} />
      <div className="flex min-w-0 flex-col gap-1 p-3">
        {logoUrl === null ? (
          <span className="font-display text-title font-bold" style={{ color }}>
            {name}
          </span>
        ) : (
          <img src={logoUrl} alt={name} className="h-7 w-auto max-w-40 self-start object-contain" />
        )}
        <span className="font-display font-bold">{t('studio.brandSampleTitle')}</span>
        <span className="text-small text-muted">{t('studio.brandSampleText')}</span>
        <span
          className="mt-1 w-fit rounded-sm px-2.5 py-1 text-small font-bold text-white"
          style={{ backgroundColor: color }}
        >
          {t('studio.brandSampleButton')}
        </span>
      </div>
    </div>
  );
}

function StudioForm({ studio, logo }: { studio: StudioDto; logo: UploadedFile | null }) {
  const queryClient = useQueryClient();
  const { can } = usePermissions();
  const canEdit = can('studio.manage');
  const suggestions = useQuery(orpc.requisites.status.queryOptions()).data?.suggestions ?? false;
  const mailDomain = useQuery(orpc.mail.me.queryOptions()).data?.domain ?? null;
  // Подписант в платформе — любой действующий сотрудник; список людей уже загружен оболочкой.
  const people = useQuery(orpc.messenger.people.queryOptions()).data ?? [];
  const signerOptions = people
    .filter((person) => person.active || person.id === studio.signerUserId)
    .map((person) => ({ value: person.id, label: person.name }));

  const form = useAppForm({
    defaultValues: toValues(studio, logo),
    validators: { onChange: schema },
    onSubmit: async ({ value, formApi }) => {
      try {
        await client.org.studio.update({
          ...toRequisites(value),
          version: studio.version,
          logoFileId: value.logo[0]?.id ?? null,
          signerUserId: value.signerUserId,
        });
        notify(t('studio.saved'));
      } catch (error) {
        formApi.setErrorMap({ onSubmit: formError(error instanceof Error ? error.message : String(error)) });
        // Конфликт версий: показываем актуальные данные — форма пересоздастся с новой версией.
        if (!(error instanceof ORPCError) || error.code !== 'common.conflict') return;
      }
      await queryClient.invalidateQueries({ queryKey: orpc.org.studio.key() });
    },
  });

  return (
    <form.AppForm>
      <form.Form className="flex flex-col gap-4">
        <fieldset disabled={!canEdit} className="contents">
          <form.Subscribe selector={(state) => state.values.legalForm}>
            {(legalForm) => {
              const individual = legalForm === 'individual';
              return (
                <SettingsSection title={t('studio.sectionCompany')}>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <form.AppField name="legalForm">
                        {(field) => (
                          <field.SegmentsField
                            label={t('studio.fields.legalForm')}
                            hint={{ text: t('studio.legalFormHint'), example: t('studio.legalFormExample') }}
                            options={LEGAL_FORMS.map((form) => ({
                              value: form,
                              label: t(`studio.legalForms.${form}`),
                            }))}
                          />
                        )}
                      </form.AppField>
                    </div>
                    <div className="sm:col-span-2">
                      <form.AppField name="inn">
                        {(field) => (
                          <field.CompanyField
                            label={t('studio.fields.inn')}
                            loadSuggestions={suggestions ? loadStudioCompanies : undefined}
                            onPick={(company) => {
                              const picked = company.kind;
                              form.setFieldValue('legalForm', picked);
                              form.setFieldValue('shortName', company.name);
                              form.setFieldValue('fullName', company.fullName ?? company.name);
                              form.setFieldValue('kpp', company.kpp ?? '');
                              form.setFieldValue('ogrn', company.ogrn ?? '');
                              form.setFieldValue('legalAddress', company.address ?? '');
                              if (company.director) form.setFieldValue('signerName', company.director.name);
                              form.setFieldValue(
                                'signerPosition',
                                picked === 'individual'
                                  ? t('studio.positionIndividual')
                                  : (company.director?.post ?? ''),
                              );
                              if (form.getFieldValue('signerBasis').trim() === '') {
                                form.setFieldValue(
                                  'signerBasis',
                                  picked === 'individual'
                                    ? t('studio.signerBasisIndividual')
                                    : t('studio.signerBasisPlaceholder'),
                                );
                              }
                            }}
                          />
                        )}
                      </form.AppField>
                    </div>
                    <form.AppField name="shortName">
                      {(field) => (
                        <field.TextField
                          label={t('studio.fields.shortName')}
                          placeholder={individual ? t('studio.shortNameIndividual') : undefined}
                        />
                      )}
                    </form.AppField>
                    {individual ? (
                      <form.AppField name="ogrn">
                        {(field) => <field.RequisiteField kind="ogrn" label={t('studio.fields.ogrnip')} />}
                      </form.AppField>
                    ) : (
                      <form.AppField name="kpp">
                        {(field) => <field.RequisiteField kind="kpp" label={t('studio.fields.kpp')} />}
                      </form.AppField>
                    )}
                    <div className="sm:col-span-2">
                      <form.AppField name="fullName">
                        {(field) => (
                          <field.TextField
                            label={t('studio.fields.fullName')}
                            placeholder={individual ? t('studio.fullNameIndividual') : undefined}
                          />
                        )}
                      </form.AppField>
                    </div>
                    {individual ? null : (
                      <form.AppField name="ogrn">
                        {(field) => <field.RequisiteField kind="ogrn" label={t('studio.fields.ogrn')} />}
                      </form.AppField>
                    )}
                    <div className="sm:col-span-2">
                      <form.AppField name="legalAddress">
                        {(field) => (
                          <field.AddressField
                            label={t('studio.fields.legalAddress')}
                            loadSuggestions={suggestions ? loadStudioAddresses : undefined}
                          />
                        )}
                      </form.AppField>
                    </div>
                  </div>
                </SettingsSection>
              );
            }}
          </form.Subscribe>

          <SettingsSection title={t('studio.sectionBank')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <form.AppField name="bankBik">
                {(field) => (
                  <field.BankField
                    label={t('studio.fields.bankBik')}
                    loadSuggestions={suggestions ? loadStudioBanks : undefined}
                    onPick={(bank) => {
                      form.setFieldValue('bankName', bank.name);
                      form.setFieldValue('bankCorrespondentAccount', bank.correspondentAccount ?? '');
                    }}
                  />
                )}
              </form.AppField>
              <form.AppField name="bankName">
                {(field) => <field.TextField label={t('studio.fields.bankName')} />}
              </form.AppField>
              <form.Subscribe selector={(state) => state.values.bankBik}>
                {(bic) => (
                  <>
                    <form.AppField name="bankAccount">
                      {(field) => <field.AccountField label={t('studio.fields.bankAccount')} bic={bic} />}
                    </form.AppField>
                    <form.AppField name="bankCorrespondentAccount">
                      {(field) => (
                        <field.AccountField
                          label={t('studio.fields.bankCorrespondentAccount')}
                          bic={bic}
                          kind="correspondent"
                        />
                      )}
                    </form.AppField>
                  </>
                )}
              </form.Subscribe>
            </div>
          </SettingsSection>

          <SettingsSection title={t('studio.sectionSigner')} lead={t('studio.signerLead')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <form.AppField name="signerName">
                {(field) => <field.TextField label={t('studio.fields.signerName')} />}
              </form.AppField>
              <form.AppField name="signerPosition">
                {(field) => <field.TextField label={t('studio.fields.signerPosition')} />}
              </form.AppField>
              <div className="sm:col-span-2">
                <form.Subscribe selector={(state) => state.values.legalForm}>
                  {(legalForm) => (
                    <form.AppField name="signerBasis">
                      {(field) => (
                        <field.TextField
                          label={t('studio.fields.signerBasis')}
                          description={t('studio.signerBasisHint')}
                          placeholder={
                            legalForm === 'individual'
                              ? t('studio.signerBasisIndividual')
                              : t('studio.signerBasisPlaceholder')
                          }
                        />
                      )}
                    </form.AppField>
                  )}
                </form.Subscribe>
              </div>
              <div className="sm:col-span-2">
                <form.AppField name="signerUserId">
                  {(field) => (
                    <field.SelectField
                      label={t('studio.fields.signerUserId')}
                      description={t('studio.signerUserHint')}
                      placeholder={t('studio.signerUserPlaceholder')}
                      options={signerOptions}
                      clearable
                    />
                  )}
                </form.AppField>
              </div>
            </div>
          </SettingsSection>

          <SettingsSection title={t('studio.sectionContacts')}>
            <div className="grid gap-3 sm:grid-cols-2">
              <form.AppField name="email">
                {(field) => <field.EmailField label={t('studio.fields.email')} />}
              </form.AppField>
              <form.AppField name="phone">
                {(field) => <field.PhoneField label={t('studio.fields.phone')} />}
              </form.AppField>
              <form.AppField name="website">
                {(field) => <field.TextField label={t('studio.fields.website')} inputMode="url" />}
              </form.AppField>
              <form.AppField name="timezone">
                {(field) => (
                  <field.SelectField
                    label={t('studio.fields.timezone')}
                    options={RU_TIME_ZONES.map((zone) => ({
                      value: zone,
                      label: t(`studio.timezones.${zone}`),
                    }))}
                  />
                )}
              </form.AppField>
              <div className="sm:col-span-2">
                <Field label={t('studio.mailDomain')} description={t('studio.mailDomainHint')} readOnly>
                  <TextInput
                    readOnly
                    value={mailDomain ?? t('studio.mailDomainNone')}
                    onValueChange={() => undefined}
                  />
                </Field>
              </div>
            </div>
          </SettingsSection>

          <SettingsSection title={t('studio.sectionBrand')} lead={t('studio.brandLead')}>
            <form.AppField name="logo">
              {(field) => (
                <field.FileField
                  label={t('studio.fields.logoFileId')}
                  hideLabel
                  upload={uploadFile}
                  accept={{ 'image/png': [], 'image/jpeg': [], 'image/webp': [], 'image/svg+xml': [] }}
                />
              )}
            </form.AppField>
            <div className="mt-3 grid gap-3 sm:grid-cols-[minmax(0,16rem)_minmax(0,1fr)] sm:items-start">
              <form.AppField name="brandColor">
                {(field) => (
                  <field.TextField
                    label={t('studio.fields.brandColor')}
                    hint={{ text: t('studio.brandColorHint'), example: t('studio.brandColorExample') }}
                    placeholder={DEFAULT_BRAND_COLOR}
                    maxLength={7}
                    autoCapitalize="characters"
                    spellCheck={false}
                  />
                )}
              </form.AppField>
              <form.Subscribe
                selector={(state) => ({ color: state.values.brandColor, logo: state.values.logo })}
              >
                {({ color, logo: current }) => (
                  <BrandSample
                    color={isBrandColor(color.trim()) ? color.trim() : DEFAULT_BRAND_COLOR}
                    logoUrl={current[0]?.previewUrl ?? null}
                    name={studio.shortName ?? studio.fullName ?? t('studio.title')}
                  />
                )}
              </form.Subscribe>
            </div>
          </SettingsSection>
        </fieldset>

        {canEdit ? (
          // Форма длинная: кнопка всегда под рукой — над нижним меню телефона и у края экрана.
          <div className="sticky bottom-[var(--nav-h)] z-[var(--z-sticky)] -mx-4 flex flex-col gap-3 border-t border-border-soft bg-bg/90 px-4 py-3 backdrop-blur md:bottom-0 md:-mx-6 md:px-6">
            <form.FormError />
            <div>
              <form.SubmitButton onlyWhenChanged>{t('studio.save')}</form.SubmitButton>
            </div>
          </div>
        ) : null}
      </form.Form>
    </form.AppForm>
  );
}
